import React from "react";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import CatalogTableC from "../../components/Catalogs/CatalogTableC";
import useDataLoader2 from "../../hooks/useDataLoader2";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import Eye from "../../components/Atomos/Icons/ViewMode/Eye";
import { defaultPageSize } from "../../utils/const";
import Filters, {
  IFilterData,
} from "../../components/Catalogs/Filters/Filters";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import type { FirmContactView } from "../../interfaces/models/IFirmContact";
import { ContactTypeString } from "../../components/Catalogs/FirmContact/FirmContactEnums";
import { exportAllDataLoader2 } from "../../utils/exportAllDataLoader2";
import { formatPhoneForDisplay } from "../../utils/formatPhoneForDisplay";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";

type ContactSlotKey = keyof typeof ContactTypeString;

const CONTACT_EXPORT_SLOTS: Array<{
  type: ContactSlotKey;
  pick: (row: FirmContactView) => FirmContactView["primaryContact"][];
}> = [
  {
    type: "primaryContact",
    pick: (row) => [row.primaryContact],
  },
  {
    type: "secondaryContact",
    pick: (row) => [row.secondaryContact],
  },
  {
    type: "accountingContact",
    pick: (row) => [row.accountingContact],
  },
  {
    type: "marketingContact",
    pick: (row) => row.marketingContacts ?? [],
  },
  {
    type: "itContact",
    pick: (row) => (row.itContact ? [row.itContact] : []),
  },
  {
    type: "socialMediaContact",
    pick: (row) => (row.socialMediaContact ? [row.socialMediaContact] : []),
  },
  {
    type: "hrContact",
    pick: (row) => (row.hrContact ? [row.hrContact] : []),
  },
  {
    type: "additionalContact",
    pick: (row) => row.liaisonContacts ?? [],
  },
];

const FirmContact: React.FC = () => {
  const excelHeaders = [
    "firm",
    "membership",
    "fullName",
    "typeOfContact",
    "phone",
    "email",
  ];

  const fields = [
    "id",
    "legacyId",
    "firmId",
    "officeId",
    "firmName",
    "jurisdiction",
    "primaryContact",
    "secondaryContact",
    "accountingContact",
    "marketingContacts",
    "itContact",
    "socialMediaContact",
    "hrContact",
    "liaisonContacts",
    "_ts",
  ];
  const endpoint = "/firmContact";

  const {
    data,
    totalCount,
    fetchData,
    setFilter,
    sort,
    setSort,
    pagination,
    setPagination,
    filter,
    inputFilters,
    setInputFilters,
  } = useDataLoader2<FirmContactView>({
    endpoint,
    defaultPageSize,
    fields,
    defaultSorting: { field: "firm", order: "asc" },
  });

  const columnsDef = React.useMemo(
    () => [
      {
        id: "count",
        header: "#",
        cell: ({ rowIndex }: { rowIndex: number; row: any; value: any }) => (
          <p> {rowIndex + 1 + pagination.pageIndex * pagination.pageSize} </p>
        ),
        enableSorting: false,
      },
      { id: "firm", header: "Firm" },
      { id: "jurisdiction", header: "Membership Location" },
      {
        id: "edit",
        header: "Edit",
        enableSorting: false,
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => (
          <PersistentLink
            className="edit"
            to={`View/${row.id}`}
            state={row}
            persistingData={{
              pagination,
              sort,
              filter,
              inputFilters,
            }}
          >
            View
            <div className="editsvg">
              <Eye />
            </div>
          </PersistentLink>
        ),
      },
    ],
    [pagination.pageIndex, pagination.pageSize, sort, filter, inputFilters],
  );

  const { firmData } = UseFirmList();

  const formatData = (rows: FirmContactView[]) =>
    rows.map((row) => ({
      ...row,
      firm: row.firmName,
      jurisdiction: getJurisdictionLabel({ jurisdiction: row.jurisdiction }) || "—",
    }));

  const formatExcelData = (rows: FirmContactView[]) => {
    const formattedRows: Array<{
      firm: string;
      membership: string;
      fullName: string;
      typeOfContact: string;
      phone: string;
      email: string;
    }> = [];

    for (const row of rows) {
      const membership = getJurisdictionLabel({ jurisdiction: row.jurisdiction });
      for (const slot of CONTACT_EXPORT_SLOTS) {
        for (const contact of slot.pick(row)) {
          const fullName = String(contact?.fullName ?? "").trim();
          if (!fullName && !contact?.id) {
            continue;
          }

          formattedRows.push({
            firm: row.firmName ?? "",
            membership,
            fullName,
            typeOfContact: ContactTypeString[slot.type] ?? slot.type,
            phone: formatPhoneForDisplay(contact?.phone) ?? "",
            email: String(contact?.email ?? "").trim(),
          });
        }
      }
    }

    return formattedRows;
  };

  const filtersData: IFilterData[] = React.useMemo(
    () => [
      {
        name: "Firm",
        objectData:
          firmData?.map((f: { id: string; name?: string; firmName?: string }) => ({
            id: f.id,
            name: f.name ?? f.firmName ?? "",
          })) || [],
      },
    ],
    [firmData],
  );

  return (
    <>
      <div>
        <CatalogTableC
          title={`Firm Contact Catalog`}
          catalogName={"FIRM CONTACTS"}
          catalogShortName="FIRM CONTACT"
          route={"FirmContact"}
          totalCount={totalCount}
        >
          <Filters
            filtersData={filtersData}
            filterValues={inputFilters}
            setFilterValues={setInputFilters}
            Search={() => fetchData()}
            clearSelections={() => fetchData(true)}
            setValueForQuery={setFilter}
          />
          <PaginatedTable
            data={data || []}
            columnsDef={columnsDef}
            formatData={formatData}
            excelHeaders={excelHeaders}
            formatExcelData={formatExcelData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={totalCount || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            endpoint={endpoint}
            onExportAll={() =>
              exportAllDataLoader2<FirmContactView>({
                endpoint,
                filter,
                sort,
                pageSize: 1000,
                fields,
              })
            }
          />
        </CatalogTableC>
      </div>
    </>
  );
};

export default FirmContact;
